/**
 * The client's replica of the workspace.
 *
 * SHAPE
 *   The store holds RAW DATA ONLY: the change log, the comments, the roster,
 *   and a cursor. Everything the interface draws — the file tree, the
 *   contribution grid, the search index, a file's history — is DERIVED from
 *   that log by a pure function in @lattice/core.
 *
 *   Deriving rather than storing is what keeps the product honest: the tree
 *   and the provenance cannot disagree, because the tree *is* the provenance,
 *   folded. It is also fast enough to be free — folding the team's entire
 *   five-month history takes single-digit milliseconds — and the results are
 *   memoised on the cursor, so a render that changes nothing recomputes
 *   nothing.
 */
import { create } from 'zustand'
import {
  type Change, type Comment, type ProjectId, type User, type UserId, type FileSet,
  type TreeNode, type Presence, type PresenceMap, type Path,
  type Lock, type LockMap,
  projectFiles, applyChangesTo, buildTree, maxSeq, mergePresence, emptyPresence, heldLocks,
} from '@lattice/core'
import type { ProjectDto } from './api'

export type Status = 'booting' | 'signed-out' | 'ready' | 'error'
export type SyncState = 'idle' | 'syncing' | 'offline'

export interface WorkspaceState {
  status: Status
  error?: string
  syncState: SyncState
  /** True once we have rendered from the local replica, before the network replies. */
  fromCache: boolean

  user?: User
  roster: User[]

  changes: Change[]
  comments: Comment[]
  /** Every lock row we have seen; fold with `locksHeldNow` for what is claimed. */
  locks: Lock[]
  projects: ProjectDto[]
  cursor: number
  /**
   * The workspace epoch our cursor belongs to.
   * Interpretation: the identity of the server's log. When it changes, our
   * cursor means nothing and the replica must be rebuilt.
   */
  epoch?: string
  /** Who is online, away or offline, keyed by person. */
  presence: PresenceMap
  /** Identifies our own open stream, for telling the server where we are. */
  connection?: string
  /** The sequence number this person had already seen when they last left. */
  readMark: number

  /** Replace everything (first load, or sign-in). */
  hydrate: (input: {
    user?: User; roster?: readonly User[]; projects?: readonly ProjectDto[]
    changes: readonly Change[]; comments: readonly Comment[]; locks?: readonly Lock[]
    cursor: number; readMark?: number
    epoch?: string; presence?: readonly Presence[]; fromCache?: boolean
  }) => void

  /** Merge a delta from the server. */
  applyDelta: (delta: {
    changes: readonly Change[]; comments: readonly Comment[]
    locks?: readonly Lock[]; cursor: number
  }) => void

  setStatus: (status: Status, error?: string) => void
  setSyncState: (syncState: SyncState) => void
  setPresence: (rows: readonly Presence[]) => void
  setConnection: (connection: string | undefined) => void
  setReadMark: (seq: number) => void
  reset: () => void
}

/**
 * Merge new rows into existing ones, keyed by id, newest sequence winning.
 *
 * The server may resend a row we already hold (a retry, an overlapping delta,
 * an edit that bumped its sequence). De-duplicating by id and keeping the
 * higher sequence makes applying a delta idempotent, which in turn means a
 * dropped connection can never corrupt the replica.
 * mergeById : [T] [T] -> [T]
 */
function mergeById<T extends { id: string; seq: number }>(
  existing: readonly T[], incoming: readonly T[],
): T[] {
  if (incoming.length === 0) return existing as T[]
  const byId = new Map(existing.map(row => [row.id, row]))
  for (const row of incoming) {
    const prior = byId.get(row.id)
    if (!prior || row.seq >= prior.seq) byId.set(row.id, row)
  }
  return [...byId.values()].sort((a, b) => a.seq - b.seq)
}

export const useWorkspace = create<WorkspaceState>()((set) => ({
  status: 'booting',
  syncState: 'idle',
  fromCache: false,
  roster: [],
  changes: [],
  comments: [],
  locks: [],
  projects: [],
  cursor: 0,
  presence: emptyPresence(new Date(0).toISOString()),
  readMark: 0,

  hydrate: input => set(state => ({
    user: input.user ?? state.user,
    roster: input.roster ? [...input.roster] : state.roster,
    projects: input.projects ? [...input.projects] : state.projects,
    changes: [...input.changes].sort((a, b) => a.seq - b.seq),
    comments: [...input.comments].sort((a, b) => a.seq - b.seq),
    locks: input.locks ? [...input.locks].sort((a, b) => a.seq - b.seq) : state.locks,
    cursor: input.cursor,
    epoch: input.epoch ?? state.epoch,
    presence: input.presence ? mergePresence(input.presence, new Date().toISOString()) : state.presence,
    readMark: input.readMark ?? state.readMark,
    fromCache: input.fromCache ?? false,
    status: 'ready',
  })),

  applyDelta: delta => set(state => {
    const locks = delta.locks ?? []
    const nothing = delta.changes.length === 0 && delta.comments.length === 0 && locks.length === 0
    if (nothing && delta.cursor <= state.cursor) {
      return { cursor: Math.max(state.cursor, delta.cursor) }
    }
    return {
      changes: mergeById(state.changes, delta.changes),
      comments: mergeById(state.comments, delta.comments),
      locks: mergeById(state.locks, locks),
      cursor: Math.max(state.cursor, delta.cursor),
    }
  }),

  setStatus: (status, error) => set({ status, error }),
  setSyncState: syncState => set({ syncState }),
  setPresence: rows => set({ presence: mergePresence(rows, new Date().toISOString()) }),
  setConnection: connection => set({ connection }),
  setReadMark: seq => set(state => ({ readMark: Math.max(state.readMark, seq) })),
  reset: () => set({
    status: 'signed-out', user: undefined, changes: [], comments: [], locks: [],
    projects: [], cursor: 0, epoch: undefined, connection: undefined,
    presence: emptyPresence(new Date(0).toISOString()), readMark: 0, fromCache: false,
  }),
}))

/* ------------------------------------------------------------------ */
/* Derived views, memoised on the log itself                           */
/* ------------------------------------------------------------------ */

/**
 * A one-slot memo keyed on object identity.
 *
 * The change log is replaced (never mutated) on every delta, so identity is a
 * correct and extremely cheap cache key: if the array is the same array, the
 * fold cannot have a different answer.
 * memoOnce : (A -> B) -> (A -> B)
 */
function memoOnce<A extends object, B>(f: (a: A) => B): (a: A) => B {
  let lastInput: A | undefined
  let lastOutput: B
  return (a: A): B => {
    if (a !== lastInput) { lastInput = a; lastOutput = f(a) }
    return lastOutput
  }
}

/** Group the whole log by project, so each project folds independently. */
export const changesByProject = memoOnce((changes: Change[]): Map<ProjectId, Change[]> => {
  const out = new Map<ProjectId, Change[]>()
  for (const c of changes) {
    const list = out.get(c.projectId)
    if (list) list.push(c)
    else out.set(c.projectId, [c])
  }
  return out
})

/** The current files of every project. filesByProject : [Change] -> Map */
export const filesByProject = memoOnce((changes: Change[]): Map<ProjectId, FileSet> => {
  const out = new Map<ProjectId, FileSet>()
  for (const [projectId, list] of changesByProject(changes)) {
    out.set(projectId, projectFiles(list))
  }
  return out
})

/** The rendered tree of every project. treesByProject : [Change] -> Map */
export const treesByProject = memoOnce((changes: Change[]): Map<ProjectId, TreeNode[]> => {
  const out = new Map<ProjectId, TreeNode[]>()
  for (const [projectId, files] of filesByProject(changes)) out.set(projectId, buildTree(files))
  return out
})

/**
 * What is claimed right now, folded from the lock log.
 * Memoised on the log's identity, like every other projection here.
 * locksHeldNow : [Lock] -> LockMap
 */
export const locksHeldNow = memoOnce((locks: Lock[]): LockMap => heldLocks(locks))

/** Comments grouped by the file they are about. commentsByPath : [Comment] -> Map */
export const commentsByPath = memoOnce((comments: Comment[]): Map<string, Comment[]> => {
  const out = new Map<string, Comment[]>()
  for (const c of comments) {
    const key = `${c.projectId}|${c.path ?? ''}`
    const list = out.get(key)
    if (list) list.push(c)
    else out.set(key, [c])
  }
  return out
})

/** Every change that has ever touched one path, newest first. */
export function historyOf(changes: readonly Change[], projectId: ProjectId, path: string): Change[] {
  const key = path.toLowerCase()
  const out: Change[] = []
  for (const c of changes) {
    if (c.projectId !== projectId) continue
    if (c.path.toLowerCase() === key || c.prevPath?.toLowerCase() === key) out.push(c)
  }
  // Follow a rename backwards so a file's history survives being renamed.
  const renamedFrom = out.find(c => c.kind === 'renamed' && c.path.toLowerCase() === key)?.prevPath
  if (renamedFrom) out.push(...historyOf(changes, projectId, renamedFrom))
  return [...new Map(out.map(c => [c.id, c])).values()].sort((a, b) => b.seq - a.seq)
}

/** Look a person up, falling back to a placeholder rather than rendering blank. */
export function personOf(roster: readonly User[], id: UserId): User {
  return roster.find(u => u.id === id)
    ?? { id, name: id, email: `${id}@bharatatomic.com`, initials: id.slice(0, 2).toUpperCase(), accent: 0 }
}

export { applyChangesTo, maxSeq }
export type { Path }
